import React, { useContext, useRef, useState, useMemo, useEffect } from 'react'
import { useQuery } from '@apollo/client'
import { Alert, Button, Modal, OutlinedInput } from '@mui/material'
import { Box } from '@mui/system'
import { ContentState, EditorState, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import htmlToDraft from 'html-to-draftjs'
import { Editor } from 'react-draft-wysiwyg'
import { isExtraMergeHTMLTags } from '@mth/constants'
import { fontFamilyEnum, EmailType } from '@mth/enums'
import { getEmailTemplateQuery } from '@mth/graphql/queries/email-template'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { UserContext } from '@mth/providers/UserContext/UserProvider'
import { EmailTemplateVariables } from '@mth/types/email-template'
import { replaceTemplateVariables } from '@mth/utils'
import { StandardResponses } from './StandardReponses/StandardResponses'
import { useStyles } from './styles'
import { Title } from '../../core/components/shared/Title/Title'

type EmailModalProps = {
  handleSubmit: (
    subject: string,
    emailBody: string,
    emailType: {
      values: string
      type: string
    },
    body: string,
    addIndex: number,
    keyText: string,
  ) => void
  handleModem: () => void
  title: string
  options: string[]
  setEmailTemplate: () => void
  type: string
  setEmailFrom: () => void
  emailFrom: () => void
  setEmailBodyInfo: () => void
  schoolYearId?: number
  midYear?: boolean
}
export const EmailModal: React.FC<EmailModalProps> = ({
  handleSubmit,
  handleModem,
  title,
  options,
  setEmailTemplate,
  type,
  setEmailFrom,
  emailFrom,
  setEmailBodyInfo,
  schoolYearId,
  midYear,
}) => {
  let actionType = ''

  if (type === 'ageIssue') {
    actionType = 'AGE_ISSUE'
  } else {
    actionType = 'MISSING_INFO'
  }

  const { me } = useContext(UserContext)
  const [standard_responses, setStandardResponses] = useState([])
  const [template, setTemplate] = useState(null)

  const [keyText, setKeyText] = useState<string>('')
  const { data: emailTemplateData } = useQuery(getEmailTemplateQuery, {
    variables: {
      template: type === 'ageIssue' ? 'Age Issue' : 'Missing Information',
      schoolYearId: schoolYearId,
      midYear: midYear,
      regionId: me?.selectedRegionId,
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (emailTemplateData !== undefined) {
      const { emailTemplateName } = emailTemplateData
      if (emailTemplateName) {
        // @ts-ignore
        setEmailTemplate(emailTemplateName)
        setTemplate(emailTemplateName)
      }
    }
  }, [emailTemplateData])

  const [subject, setSubject] = useState('')

  const [alert, setAlert] = useState(false)
  const [currentBlocks, setCurrentBlocks] = useState(0)
  // @ts-ignore
  const defaultEmail = useMemo(() => setEmailBodyInfo(''), [])
  const [editorState, setEditorState] = useState(
    // @ts-ignore
    EditorState.createWithContent(ContentState.createFromText(defaultEmail)),
  )
  const [body, setBody] = useState('')

  const editorRef = useRef(null)
  const classes = useStyles

  useEffect(() => {
    if (template) {
      const { subject, from, body } = template
      // @ts-ignore
      setSubject(setEmailBodyInfo(subject))
      // @ts-ignore
      setEmailFrom(from)
      if (body) {
        setBody(body)
        // @ts-ignore
        const contentBlock = htmlToDraft(setEmailBodyInfo(body))
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
          setEditorState(EditorState.createWithContent(contentState))
        }
      }
    }
  }, [template])

  useEffect(() => {
    let HtmlInput: string = body

    //  find the second </p>
    const index = HtmlInput.indexOf('</p>', HtmlInput.indexOf('</p>') + 1) + 4

    setKeyText(HtmlInput.slice(index, index + 20))

    let standardResponseExtraText = '',
      standardResponseExtraSpan = ''
    for (let i = 0; i < standard_responses.length; i++) {
      if (type === EmailType.AGE_ISSUE) {
        // @ts-ignore
        standardResponseExtraText += `<li>${standard_responses[i].title?.trim()}`

        // @ts-ignore
        if (standard_responses[i].extraText.trim() !== '') {
          // @ts-ignore
          standardResponseExtraText += `<ul class="sub-bullet"><li>${standard_responses[i].extraText
            ?.trim()
            ?.replace(isExtraMergeHTMLTags, '')}</li></ul>`
        }
        standardResponseExtraText += '</li>'
      } else if (type === EmailType.MISSING_INFO) {
        // @ts-ignore
        standardResponseExtraSpan += `<li>${standard_responses[i].title?.trim()}`

        // @ts-ignore
        if (standard_responses[i].extraText.trim() !== '') {
          // @ts-ignore
          standardResponseExtraSpan += `<ul class="sub-bullet"><li>${standard_responses[i].extraText
            ?.trim()
            ?.replace(isExtraMergeHTMLTags, '')}</li></ul>`
        }
        standardResponseExtraSpan += '</li>'
      }
    }

    const replacements: EmailTemplateVariables = {
      instructions: standardResponseExtraText,
      files: `<ul class="primary-bullet">${standardResponseExtraSpan}</ul>`,
    }
    HtmlInput = replaceTemplateVariables(HtmlInput, replacements)

    // @ts-ignore
    HtmlInput = setEmailBodyInfo(HtmlInput)

    const contentBlock = htmlToDraft(HtmlInput)

    if (contentBlock) {
      const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks)
      setEditorState(EditorState.createWithContent(contentState))
    }
  }, [body, standard_responses])

  // @ts-ignore
  const handleEditorChange = (state) => {
    try {
      if (currentBlocks !== 0 && currentBlocks !== state.blocks.length) {
        // @ts-ignore
        editorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
      setCurrentBlocks(state.blocks.length)
    } catch {}
  }

  const onSubmit = () => {
    const email: string = draftToHtml(convertToRaw(editorState.getCurrentContent()))
    if (email.search(/\[BLANK\]/g) >= 0) {
      setAlert(true)
      return
    }
    if (handleSubmit) {
      // @ts-ignore
      handleSubmit(
        subject,
        draftToHtml(convertToRaw(editorState.getCurrentContent())),
        {
          values: standard_responses,
          type: actionType,
        },
        body,
        keyText,
      )
    }
    // @ts-ignore
    handleModem(false)
  }

  return (
    <Modal
      open={true}
      onClose={() => handleModem()}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={classes.modalCard}>
        <Title fontWeight='700'>{title}</Title>
        <Box sx={{ padding: '40px', paddingBottom: alert ? '20px' : undefined }}>
          {options && (
            <StandardResponses
              // @ts-ignore
              options={options}
              type={type}
              setBody={setBody}
              standardResponses={standard_responses}
              // @ts-ignore
              setStandardResponses={setStandardResponses}
            />
          )}

          <OutlinedInput
            value={emailFrom}
            size='small'
            fullWidth
            // @ts-ignore
            onChange={(e) => setEmailFrom(e.target.value)}
            placeholder='From email'
            sx={classes.subject}
            // disabled
          />

          <OutlinedInput
            value={subject}
            size='small'
            fullWidth
            placeholder='Subject'
            sx={classes.subject}
            // @ts-ignore
            onChange={(e) => setSubject(setEmailBodyInfo(e.target.value))}
          />
          <Box sx={classes.editor}>
            <Editor
              onContentStateChange={handleEditorChange}
              // @ts-ignore
              editorRef={(ref) => (editorRef.current = ref)}
              editorState={editorState}
              onEditorStateChange={setEditorState}
              handlePastedText={() => false}
              toolbar={{
                options: [
                  'inline',
                  'blockType',
                  'fontSize',
                  'fontFamily',
                  'list',
                  'textAlign',
                  'colorPicker',
                  'link',
                  'embedded' /*, 'emoji'*/,
                  'image',
                  'remove',
                  'history',
                ],
                fontFamily: {
                  options: Object.values(fontFamilyEnum),
                  className: undefined,
                  component: undefined,
                  dropdownClassName: undefined,
                },
              }}
            />
          </Box>
        </Box>

        <Alert
          onClose={() => setAlert(false)}
          sx={{ marginBottom: '15px', display: alert ? 'flex' : 'none' }}
          severity='error'
        >
          Please replace the [BLANK] text in the specific instructions.
        </Alert>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <Button
            variant='contained'
            color='secondary'
            disableElevation
            sx={classes.cancelButton}
            onClick={handleModem}
          >
            Cancel
          </Button>
          <Button variant='contained' disableElevation sx={classes.submitButton} onClick={onSubmit}>
            Send
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
